import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import {
  Save,
  RefreshCw,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Eye,
  EyeOff,
  TestTube2,
  Trash2,
  Clock,
  Info,
  Bug,
  ChevronDown,
  ChevronRight,
  ArrowLeftRight,
  Layers,
  Play,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

// Build backend URL: use env var if set (add https:// if missing), fallback to same origin
let BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
if (BACKEND_URL && !BACKEND_URL.startsWith('http')) {
  BACKEND_URL = `https://${BACKEND_URL}`;
}
if (!BACKEND_URL) {
  BACKEND_URL = window.location.origin;
}
const API = `${BACKEND_URL}/api`;

// Debug
console.log('Settings: BACKEND_URL =', BACKEND_URL);
console.log('Settings: API =', API);

export default function Settings() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [showTokens, setShowTokens] = useState({});
  const [bcCompanies, setBcCompanies] = useState([]);
  const [debugEntity, setDebugEntity] = useState("contacts");
  const [debugResult, setDebugResult] = useState(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const [debugExpanded, setDebugExpanded] = useState({});
  const [odataDebugResult, setOdataDebugResult] = useState(null);
  const [odataDebugLoading, setOdataDebugLoading] = useState(false);
  
  const [hubspotConfig, setHubspotConfig] = useState({
    hubspot_token: "",
    is_connected: false,
    last_tested: null,
    error_message: null,
  });
  
  const [bcConfig, setBcConfig] = useState({
    bc_client_id: "",
    bc_client_secret: "",
    bc_tenant_id: "",
    bc_environment: "",
    bc_company_name: "",
    bc_odata_endpoint: "",
    is_connected: false,
    last_tested: null,
    error_message: null,
  });

  const [schedules, setSchedules] = useState([]);
  const [objectMappings, setObjectMappings] = useState([]);
  const [syncing, setSyncing] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [previewing, setPreviewing] = useState(null);
  const [previewResult, setPreviewResult] = useState(null);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);

  useEffect(() => {
    fetchConnections();
    fetchSchedules();
    fetchObjectMappings();
  }, []);

  const fetchConnections = async () => {
    setLoading(true);
    try {
      console.log('Fetching connections from:', `${API}/connections`);
      const response = await axios.get(`${API}/connections`);
      console.log('Connections response:', response.data);
      
      // Ensure we have an array
      let connections = [];
      if (Array.isArray(response.data)) {
        connections = response.data;
      } else if (response.data && typeof response.data === 'object') {
        // Maybe it's wrapped in an object
        connections = response.data.data || response.data.results || [];
      }
      
      const hubspot = connections.find(c => c && c.type === "hubspot");
      const bc = connections.find(c => c && c.type === "business_central");
      
      if (hubspot) {
        setHubspotConfig({
          hubspot_token: hubspot.hubspot_token || "",
          is_connected: hubspot.is_connected || false,
          last_tested: hubspot.last_tested,
          error_message: hubspot.error_message,
        });
      }
      
      if (bc) {
        setBcConfig({
          bc_client_id: bc.bc_client_id || "",
          bc_client_secret: bc.bc_client_secret || "",
          bc_tenant_id: bc.bc_tenant_id || "",
          bc_environment: bc.bc_environment || "",
          bc_company_name: bc.bc_company_name || "",
          bc_odata_endpoint: bc.bc_odata_endpoint || "",
          is_connected: bc.is_connected || false,
          last_tested: bc.last_tested,
          error_message: bc.error_message,
        });
        // Auto-load BC companies if already connected
        if (bc.is_connected && bc.bc_client_id) {
          try {
            const companiesRes = await axios.get(`${API}/bc/companies`);
            const list = companiesRes.data || [];
            if (Array.isArray(list) && list.length > 0) {
              setBcCompanies(list.map(c => ({ name: c.name || "", id: c.id || "" })));
            }
          } catch (e) {
            console.warn("Could not auto-load BC companies:", e);
          }
        }
      }
    } catch (error) {
      console.error("Failed to fetch connections:", error);
      toast.error("Erreur de connexion à l'API");
    } finally {
      setLoading(false);
    }
  };

  const fetchSchedules = async () => {
    try {
      console.log('Fetching schedules from:', `${API}/sync/schedules`);
      const response = await axios.get(`${API}/sync/schedules`);
      console.log('Schedules response:', response.data);
      
      if (Array.isArray(response.data)) {
        setSchedules(response.data);
      } else {
        console.warn('Schedules response is not an array:', response.data);
        setSchedules([]);
      }
    } catch (error) {
      console.error("Failed to fetch schedules:", error);
      setSchedules([]);
    }
  };

  const fetchObjectMappings = async () => {
    try {
      const response = await axios.get(`${API}/object-mappings`);
      setObjectMappings(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Failed to fetch object mappings:", error);
      setObjectMappings([]);
    }
  };

  const handlePreviewSync = async (scheduleKey) => {
    setPreviewing(scheduleKey);
    try {
      const response = await axios.post(`${API}/sync/preview?object_type=${encodeURIComponent(scheduleKey)}&direction=bidirectional`);
      setPreviewResult({ ...response.data, scheduleKey });
      setShowPreviewDialog(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'aperçu");
    } finally {
      setPreviewing(null);
    }
  };

  const handleManualSync = async (scheduleKey) => {
    setSyncing(scheduleKey);
    setShowPreviewDialog(false);
    try {
      const response = await axios.post(`${API}/sync/manual?object_type=${encodeURIComponent(scheduleKey)}&direction=bidirectional`);
      const data = response.data;
      if (data.status === "failed") {
        toast.error(data.message || "Erreur lors de la synchronisation");
      } else {
        setSyncResult(data);
        const d = data.details || {};
        if (d.records_synced > 0) {
          toast.success(`Synchronisation terminée : ${d.records_synced} enregistrements synchronisés${d.records_failed ? `, ${d.records_failed} erreurs` : ''}`);
        } else if (d.records_skipped > 0) {
          toast.warning(`Aucune correspondance trouvée (${d.records_skipped} ignorés). Vérifiez les clés de correspondance.`);
        } else {
          toast.success(data.message || "Synchronisation terminée");
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la synchronisation");
    } finally {
      setSyncing(null);
    }
  };

  const saveHubspotConfig = async () => {
    setSaving(true);
    try {
      console.log('Saving HubSpot config to:', `${API}/connections`);
      const response = await axios.post(`${API}/connections`, {
        type: "hubspot",
        hubspot_token: hubspotConfig.hubspot_token,
      });
      console.log('HubSpot save response:', response.data);
      if (response.data && response.data.is_connected) {
        toast.success("Connexion HubSpot réussie!");
      } else {
        const errMsg = response.data?.error_message || "Échec de la connexion HubSpot (pas de détail retourné par le backend)";
        toast.error(errMsg);
        setHubspotConfig(prev => ({ ...prev, error_message: errMsg, is_connected: false }));
      }
      await fetchConnections();
    } catch (error) {
      console.error("HubSpot save error:", error);
      const detail = error.response ? `HTTP ${error.response.status}: ${JSON.stringify(error.response.data)}` : error.message;
      const errMsg = `Erreur réseau vers le backend: ${detail} (URL: ${API}/connections)`;
      toast.error(errMsg);
      setHubspotConfig(prev => ({ ...prev, error_message: errMsg, is_connected: false }));
    } finally {
      setSaving(false);
    }
  };

  const saveBcConfig = async () => {
    setSaving(true);
    try {
      console.log('Saving BC config to:', `${API}/connections`);
      const response = await axios.post(`${API}/connections`, {
        type: "business_central",
        bc_client_id: bcConfig.bc_client_id,
        bc_client_secret: bcConfig.bc_client_secret,
        bc_tenant_id: bcConfig.bc_tenant_id,
        bc_environment: bcConfig.bc_environment,
        bc_company_name: bcConfig.bc_company_name,
        bc_odata_endpoint: bcConfig.bc_odata_endpoint,
      });
      console.log('BC save response:', response.data);
      if (response.data && response.data.is_connected) {
        toast.success("Connexion Business Central réussie!");
      } else {
        const errMsg = response.data?.error_message || "Échec de la connexion BC (pas de détail retourné par le backend)";
        toast.error(errMsg);
        setBcConfig(prev => ({ ...prev, error_message: errMsg, is_connected: false }));
      }
      await fetchConnections();
    } catch (error) {
      console.error("BC save error:", error);
      const detail = error.response ? `HTTP ${error.response.status}: ${JSON.stringify(error.response.data)}` : error.message;
      const errMsg = `Erreur réseau vers le backend: ${detail} (URL: ${API}/connections)`;
      toast.error(errMsg);
      setBcConfig(prev => ({ ...prev, error_message: errMsg, is_connected: false }));
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async (type) => {
    setTesting(type);
    try {
      console.log('Testing connection:', `${API}/connections/${type}/test`);
      const response = await axios.post(`${API}/connections/${type}/test`);
      console.log('Test response:', response.data);
      if (response.data && response.data.is_connected) {
        toast.success(`Connexion ${type === 'hubspot' ? 'HubSpot' : 'Business Central'} réussie!`);
      } else {
        const errMsg = response.data?.error_message || `Échec de la connexion (pas de détail)`;
        toast.error(errMsg);
      }
      // Capture BC companies list
      if (response.data?.bc_companies) {
        setBcCompanies(response.data.bc_companies);
      }
      await fetchConnections();
    } catch (error) {
      console.error("Test connection error:", error);
      const detail = error.response ? `HTTP ${error.response.status}: ${JSON.stringify(error.response.data)}` : error.message;
      toast.error(`Erreur réseau: ${detail}`);
    } finally {
      setTesting(null);
    }
  };

  const deleteConnection = async (type) => {
    try {
      await axios.delete(`${API}/connections/${type}`);
      toast.success("Connexion supprimée");
      await fetchConnections();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const saveSchedule = async (objectType, frequency, isActive) => {
    try {
      await axios.post(`${API}/sync/schedules`, {
        object_type: objectType,
        frequency: frequency,
        is_active: isActive,
      });
      toast.success("Planification enregistrée");
      await fetchSchedules();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const toggleShowToken = (key) => {
    setShowTokens(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const runBcDebug = async () => {
    setDebugLoading(true);
    setDebugResult(null);
    try {
      const res = await axios.get(`${API}/bc/debug/${debugEntity}`);
      setDebugResult(res.data);
    } catch (err) {
      setDebugResult({
        error: err.response?.data?.detail || err.message,
        steps: [],
      });
    } finally {
      setDebugLoading(false);
    }
  };

  const runOdataDebug = async () => {
    setOdataDebugLoading(true);
    setOdataDebugResult(null);
    try {
      const res = await axios.get(`${API}/bc/debug-odata-service`);
      setOdataDebugResult(res.data);
    } catch (err) {
      setOdataDebugResult({
        error: err.response?.data?.detail || err.message,
      });
    } finally {
      setOdataDebugLoading(false);
    }
  };

  const toggleDebugStep = (idx) => {
    setDebugExpanded((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  // Safe schedules array for rendering
  const safeSchedules = Array.isArray(schedules) ? schedules : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="settings-page">
      {/* Page Header */}
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight">
          Paramètres
        </h1>
        <p className="mt-1 text-slate-500">
          Configurez vos connexions API et planifications de synchronisation
        </p>
        {!BACKEND_URL && (
          <p className="mt-2 text-sm text-amber-600 bg-amber-50 p-2 rounded">
            ⚠️ REACT_APP_BACKEND_URL non configuré - API appelée sur le même domaine
          </p>
        )}
      </div>

      <Tabs defaultValue="connections" className="space-y-6">
        <TabsList>
          <TabsTrigger value="connections" data-testid="tab-connections">Connexions</TabsTrigger>
          <TabsTrigger value="schedules" data-testid="tab-schedules">Planification</TabsTrigger>
          {user?.role === "admin" && (
            <TabsTrigger value="debug" data-testid="tab-debug">Debug BC</TabsTrigger>
          )}
        </TabsList>

        {/* Connections Tab */}
        <TabsContent value="connections" className="space-y-6">
          {/* HubSpot Connection */}
          <Card className="border-l-4 border-l-[#FF7A59]">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-[#FF7A59]/10">
                    <svg className="w-6 h-6 text-[#FF7A59]" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.164 10.574c.727-.39 1.22-1.134 1.22-1.993 0-1.255-1.027-2.273-2.293-2.273h-.014V4.435a1.09 1.09 0 00-1.09-1.09 1.09 1.09 0 00-1.09 1.09v1.873H9.103V4.435a1.09 1.09 0 00-1.09-1.09 1.09 1.09 0 00-1.09 1.09v1.873h-.014c-1.266 0-2.293 1.018-2.293 2.273 0 .86.493 1.603 1.22 1.993a2.276 2.276 0 00-1.22 1.993c0 1.255 1.027 2.273 2.293 2.273h.014v1.873a1.09 1.09 0 001.09 1.09 1.09 1.09 0 001.09-1.09v-1.873h5.794v1.873a1.09 1.09 0 001.09 1.09 1.09 1.09 0 001.09-1.09v-1.873h.014c1.266 0 2.293-1.018 2.293-2.273 0-.86-.493-1.603-1.22-1.993z"/>
                    </svg>
                  </div>
                  <div>
                    <CardTitle className="font-heading">HubSpot</CardTitle>
                    <CardDescription>Connexion via Private App Token</CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {hubspotConfig.is_connected ? (
                    <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Connecté
                    </Badge>
                  ) : (
                    <Badge className="bg-slate-50 text-slate-600 border border-slate-200">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      Non connecté
                    </Badge>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="hubspot-token">Private App Token</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      id="hubspot-token"
                      type={showTokens.hubspot ? "text" : "password"}
                      value={hubspotConfig.hubspot_token}
                      onChange={(e) => setHubspotConfig({ ...hubspotConfig, hubspot_token: e.target.value })}
                      placeholder="pat-na1-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      className="pr-10"
                      data-testid="hubspot-token-input"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full"
                      onClick={() => toggleShowToken('hubspot')}
                    >
                      {showTokens.hubspot ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-slate-500">
                  Obtenez votre token dans HubSpot → Settings → Integrations → Private Apps
                </p>
              </div>

              {hubspotConfig.error_message && (
                <div className="p-4 rounded-md bg-rose-50 border-2 border-rose-300">
                  <p className="text-sm font-bold text-rose-800 mb-1">Erreur de connexion HubSpot:</p>
                  <p className="text-sm text-rose-700 break-all">{hubspotConfig.error_message}</p>
                  <p className="text-xs text-rose-500 mt-2">Backend URL: {API}</p>
                </div>
              )}

              {hubspotConfig.last_tested && (
                <p className="text-xs text-slate-500">
                  Dernier test: {new Date(hubspotConfig.last_tested).toLocaleString('fr-FR')}
                </p>
              )}

              <div className="flex gap-2 pt-2">
                <Button onClick={saveHubspotConfig} disabled={saving} data-testid="save-hubspot-btn">
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => testConnection('hubspot')}
                  disabled={testing === 'hubspot' || !hubspotConfig.hubspot_token}
                  data-testid="test-hubspot-btn"
                >
                  {testing === 'hubspot' ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <TestTube2 className="w-4 h-4 mr-2" />
                  )}
                  Tester
                </Button>
                {hubspotConfig.hubspot_token && (
                  <Button 
                    variant="ghost" 
                    className="text-rose-500 hover:text-rose-700"
                    onClick={() => deleteConnection('hubspot')}
                    data-testid="delete-hubspot-btn"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Business Central Connection */}
          <Card className="border-l-4 border-l-[#00B7C3]">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-[#00B7C3]/10">
                    <svg className="w-6 h-6 text-[#00B7C3]" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                    </svg>
                  </div>
                  <div>
                    <CardTitle className="font-heading">Business Central</CardTitle>
                    <CardDescription>Connexion OAuth2 via Azure AD</CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {bcConfig.is_connected ? (
                    <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Connecté
                    </Badge>
                  ) : (
                    <Badge className="bg-slate-50 text-slate-600 border border-slate-200">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      Non connecté
                    </Badge>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Info Box */}
              <div className="p-3 rounded-md bg-blue-50 border border-blue-200">
                <div className="flex gap-2">
                  <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-blue-700">
                    <p className="font-medium">Format de l'URL BC:</p>
                    <code className="text-xs bg-blue-100 px-1 rounded">
                      https://api.businesscentral.dynamics.com/v2.0/[tenant_id]/[environment]/...
                    </code>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="bc-tenant-id">Tenant ID</Label>
                  <Input
                    id="bc-tenant-id"
                    type="text"
                    value={bcConfig.bc_tenant_id}
                    onChange={(e) => setBcConfig({ ...bcConfig, bc_tenant_id: e.target.value })}
                    placeholder="be14bbbd-14ad-4360-bebc-0e03b3e8b53c"
                    data-testid="bc-tenant-id-input"
                  />
                  <p className="text-xs text-slate-500">Visible dans l'URL d'accès BC ou Azure Portal</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bc-environment">Environment</Label>
                  <Input
                    id="bc-environment"
                    type="text"
                    value={bcConfig.bc_environment}
                    onChange={(e) => setBcConfig({ ...bcConfig, bc_environment: e.target.value })}
                    placeholder="CPY280126 ou production"
                    data-testid="bc-environment-input"
                  />
                  <p className="text-xs text-slate-500">Nom de l'environnement BC (ex: CPY280126)</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="bc-client-id">Client ID (Application ID)</Label>
                  <Input
                    id="bc-client-id"
                    type="text"
                    value={bcConfig.bc_client_id}
                    onChange={(e) => setBcConfig({ ...bcConfig, bc_client_id: e.target.value })}
                    placeholder="52c66fe4-f5d1-431f-8bc0-1ae0ed8cf951"
                    data-testid="bc-client-id-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bc-client-secret">Client Secret</Label>
                  <div className="relative">
                    <Input
                      id="bc-client-secret"
                      type={showTokens.bcSecret ? "text" : "password"}
                      value={bcConfig.bc_client_secret}
                      onChange={(e) => setBcConfig({ ...bcConfig, bc_client_secret: e.target.value })}
                      placeholder="Votre client secret"
                      className="pr-10"
                      data-testid="bc-client-secret-input"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full"
                      onClick={() => toggleShowToken('bcSecret')}
                    >
                      {showTokens.bcSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="bc-company-name">Company Name</Label>
                  {bcCompanies.length > 0 ? (
                    <select
                      id="bc-company-name"
                      value={bcConfig.bc_company_name}
                      onChange={(e) => setBcConfig({ ...bcConfig, bc_company_name: e.target.value })}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      data-testid="bc-company-name-input"
                    >
                      <option value="">-- Sélectionner une compagnie --</option>
                      {bcCompanies.map((c) => (
                        <option key={c.id} value={c.name}>{c.name}</option>
                      ))}
                    </select>
                  ) : (
                    <Input
                      id="bc-company-name"
                      type="text"
                      value={bcConfig.bc_company_name}
                      onChange={(e) => setBcConfig({ ...bcConfig, bc_company_name: e.target.value })}
                      placeholder="Testez la connexion pour voir les compagnies"
                      data-testid="bc-company-name-input"
                    />
                  )}
                  <p className="text-xs text-slate-500">
                    {bcCompanies.length > 0
                      ? `${bcCompanies.length} compagnie(s) disponible(s) — sélectionnez puis enregistrez`
                      : "Testez la connexion BC pour charger la liste des compagnies"}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bc-odata-endpoint">OData Web Service (optionnel)</Label>
                  <Input
                    id="bc-odata-endpoint"
                    type="text"
                    value={bcConfig.bc_odata_endpoint}
                    onChange={(e) => setBcConfig({ ...bcConfig, bc_odata_endpoint: e.target.value })}
                    placeholder="HubspotMgtWs_CreateAndUpdateCustomer"
                    data-testid="bc-odata-endpoint-input"
                  />
                  <p className="text-xs text-slate-500">Nom du web service OData personnalisé</p>
                </div>
              </div>

              {bcConfig.error_message && (
                <div className="p-4 rounded-md bg-rose-50 border-2 border-rose-300">
                  <p className="text-sm font-bold text-rose-800 mb-1">Erreur de connexion Business Central:</p>
                  <p className="text-sm text-rose-700 break-all">{bcConfig.error_message}</p>
                  <p className="text-xs text-rose-500 mt-2">Backend URL: {API}</p>
                </div>
              )}

              {bcConfig.last_tested && (
                <p className="text-xs text-slate-500">
                  Dernier test: {new Date(bcConfig.last_tested).toLocaleString('fr-FR')}
                </p>
              )}

              <div className="flex gap-2 pt-2">
                <Button onClick={saveBcConfig} disabled={saving} data-testid="save-bc-btn">
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer et Tester
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => testConnection('business_central')}
                  disabled={testing === 'business_central' || !bcConfig.bc_client_id}
                  data-testid="test-bc-btn"
                >
                  {testing === 'business_central' ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <TestTube2 className="w-4 h-4 mr-2" />
                  )}
                  Tester
                </Button>
                {bcConfig.bc_client_id && (
                  <Button 
                    variant="ghost" 
                    className="text-rose-500 hover:text-rose-700"
                    onClick={() => deleteConnection('business_central')}
                    data-testid="delete-bc-btn"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Schedules Tab */}
        <TabsContent value="schedules" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Planification des Synchronisations</CardTitle>
              <CardDescription>
                Configurez la fréquence de synchronisation automatique pour chaque type d'objet
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {objectMappings.length > 0 ? (
                objectMappings.map((mapping) => {
                  const scheduleKey = `${mapping.hubspot_object}_${mapping.bc_entity}`;
                  const schedule = safeSchedules.find(s => s && s.object_type === scheduleKey);
                  return (
                    <div key={mapping.id} className="flex items-center justify-between p-4 rounded-lg border border-slate-200">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          schedule?.is_active ? 'bg-indigo-50' : 'bg-slate-100'
                        }`}>
                          <Clock className={`w-5 h-5 ${schedule?.is_active ? 'text-indigo-600' : 'text-slate-400'}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-900">{mapping.hubspot_object_label}</span>
                            <ArrowLeftRight className="w-3.5 h-3.5 text-slate-400" />
                            <span className="font-medium text-slate-900">{mapping.bc_entity_label}</span>
                          </div>
                          <p className="text-sm text-slate-500">
                            {schedule?.is_active
                              ? `Sync ${schedule.frequency === 'hourly' ? 'toutes les heures' :
                                  schedule.frequency === 'daily' ? 'quotidienne' : 'hebdomadaire'}`
                              : 'Non planifié'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1.5"
                          disabled={previewing === scheduleKey || syncing === scheduleKey}
                          onClick={() => handlePreviewSync(scheduleKey)}
                          data-testid={`preview-sync-${scheduleKey}`}
                        >
                          {previewing === scheduleKey ? (
                            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Eye className="w-3.5 h-3.5" />
                          )}
                          Aperçu
                        </Button>
                        <Button
                          variant="default"
                          size="sm"
                          className="gap-1.5"
                          disabled={syncing === scheduleKey || previewing === scheduleKey}
                          onClick={() => handleManualSync(scheduleKey)}
                          data-testid={`manual-sync-${scheduleKey}`}
                        >
                          {syncing === scheduleKey ? (
                            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Play className="w-3.5 h-3.5" />
                          )}
                          Sync
                        </Button>
                        <div className="w-px h-6 bg-slate-200" />
                        <Select
                          value={schedule?.frequency || "daily"}
                          onValueChange={(value) => saveSchedule(scheduleKey, value, schedule?.is_active || false)}
                        >
                          <SelectTrigger className="w-32" data-testid={`schedule-freq-${scheduleKey}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="hourly">Horaire</SelectItem>
                            <SelectItem value="daily">Quotidien</SelectItem>
                            <SelectItem value="weekly">Hebdomadaire</SelectItem>
                          </SelectContent>
                        </Select>
                        <Switch
                          checked={schedule?.is_active || false}
                          onCheckedChange={(checked) => saveSchedule(scheduleKey, schedule?.frequency || "daily", checked)}
                          data-testid={`schedule-toggle-${scheduleKey}`}
                        />
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8">
                  <Layers className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500 font-medium">Aucun mapping d'objets configuré</p>
                  <p className="text-sm text-slate-400 mt-1">
                    Configurez des mappings d'objets dans la page Field Mapping pour pouvoir planifier les synchronisations.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-base">Note importante</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600">
                Les synchronisations planifiées nécessitent que les deux connexions (HubSpot et Business Central) 
                soient configurées et testées avec succès. La planification crée des tâches qui s'exécuteront 
                automatiquement selon la fréquence définie.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Debug BC Tab (admin only) */}
        {user?.role === "admin" && (
          <TabsContent value="debug" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Bug className="w-5 h-5" />
                  Debug - Champs Business Central
                </CardTitle>
                <CardDescription>
                  Teste la découverte dynamique des champs BC étape par étape
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="space-y-1">
                    <Label>Entité BC</Label>
                    <select
                      value={debugEntity}
                      onChange={(e) => setDebugEntity(e.target.value)}
                      className="flex h-10 w-48 rounded-md border border-input bg-background px-3 py-2 text-sm"
                    >
                      <option value="contacts">Contacts</option>
                      <option value="customers">Customers</option>
                      <option value="vendors">Vendors</option>
                      <option value="items">Items</option>
                      <option value="salesOrders">Sales Orders</option>
                      <option value="salesOrderLines">Sales Order Lines</option>
                      <option value="salesInvoices">Sales Invoices</option>
                      <option value="purchaseOrders">Purchase Orders</option>
                      <option value="employees">Employees</option>
                      <option value="generalLedgerEntries">GL Entries</option>
                    </select>
                  </div>
                  <div className="pt-6">
                    <Button onClick={runBcDebug} disabled={debugLoading}>
                      {debugLoading ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <TestTube2 className="w-4 h-4 mr-2" />
                      )}
                      Tester la découverte
                    </Button>
                  </div>
                </div>

                {debugResult && (
                  <div className="space-y-3 mt-4">
                    {debugResult.error && (
                      <div className="p-3 rounded-md bg-rose-50 border border-rose-200">
                        <p className="text-sm font-medium text-rose-800">Erreur: {debugResult.error}</p>
                      </div>
                    )}

                    {debugResult.steps?.map((step, idx) => {
                      const isExpanded = debugExpanded[idx];
                      const isError = step.status === "error";
                      const isOk = step.status === "ok";
                      const hasDetail = step.body || step.data || step.companies || step.fields || step.available;

                      return (
                        <div
                          key={idx}
                          className={`rounded-md border ${
                            isError ? "border-rose-200 bg-rose-50" :
                            isOk ? "border-emerald-200 bg-emerald-50" :
                            "border-slate-200 bg-slate-50"
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => hasDetail && toggleDebugStep(idx)}
                            className="w-full flex items-center justify-between p-3 text-left"
                          >
                            <div className="flex items-center gap-2">
                              {isError ? (
                                <XCircle className="w-4 h-4 text-rose-500" />
                              ) : isOk ? (
                                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                              ) : (
                                <Info className="w-4 h-4 text-slate-400" />
                              )}
                              <span className="text-sm font-medium text-slate-800">
                                {step.step}
                              </span>
                              {step.count !== undefined && (
                                <Badge variant="secondary" className="ml-2">{step.count}</Badge>
                              )}
                              {step.type && (
                                <Badge variant="outline" className="ml-2">{step.type}</Badge>
                              )}
                              {step.status_code && (
                                <Badge variant={step.status_code === 200 ? "default" : "destructive"} className="ml-2">
                                  HTTP {step.status_code}
                                </Badge>
                              )}
                              {step.value && (
                                <span className="text-xs text-slate-500 ml-2 truncate max-w-md">{step.value}</span>
                              )}
                              {step.url && (
                                <span className="text-xs text-slate-500 ml-2 truncate max-w-md">{step.url}</span>
                              )}
                              {step.matched && (
                                <span className="text-xs text-emerald-600 ml-2">{step.matched}</span>
                              )}
                              {step.configured && (
                                <span className="text-xs text-rose-600 ml-2">"{step.configured}" non trouvé</span>
                              )}
                            </div>
                            {hasDetail && (
                              isExpanded ?
                                <ChevronDown className="w-4 h-4 text-slate-400" /> :
                                <ChevronRight className="w-4 h-4 text-slate-400" />
                            )}
                          </button>

                          {isExpanded && hasDetail && (
                            <div className="px-3 pb-3 border-t border-slate-200">
                              <pre className="mt-2 text-xs bg-white p-3 rounded overflow-auto max-h-80 whitespace-pre-wrap">
                                {JSON.stringify(
                                  step.data || step.body || step.companies || step.fields || step.available,
                                  null, 2
                                )}
                              </pre>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* OData Service Debug */}
            <Card>
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Layers className="w-5 h-5" />
                  Debug - Service OData Custom
                </CardTitle>
                <CardDescription>
                  Découvre les paramètres acceptés par le service OData configuré
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4">
                  <Button onClick={runOdataDebug} disabled={odataDebugLoading}>
                    {odataDebugLoading ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <TestTube2 className="w-4 h-4 mr-2" />
                    )}
                    Analyser le service OData
                  </Button>
                  {bcConfig.bc_odata_endpoint && (
                    <span className="text-sm text-slate-500">
                      Service configuré : <code className="bg-slate-100 px-1.5 py-0.5 rounded">{bcConfig.bc_odata_endpoint}</code>
                    </span>
                  )}
                </div>

                {odataDebugResult && (
                  <div className="space-y-3 mt-4">
                    {odataDebugResult.error && (
                      <div className="p-3 rounded-md bg-rose-50 border border-rose-200">
                        <p className="text-sm font-medium text-rose-800">Erreur: {odataDebugResult.error}</p>
                        {odataDebugResult.hint && (
                          <p className="text-xs text-rose-600 mt-1">{odataDebugResult.hint}</p>
                        )}
                      </div>
                    )}

                    {odataDebugResult.odata_endpoint && (
                      <div className="p-3 rounded-md bg-blue-50 border border-blue-200">
                        <p className="text-sm text-blue-800">
                          <strong>Endpoint:</strong> {odataDebugResult.odata_endpoint}
                        </p>
                        {odataDebugResult.odata_url && (
                          <p className="text-xs text-blue-600 mt-1 font-mono break-all">
                            URL: {odataDebugResult.odata_url}
                          </p>
                        )}
                      </div>
                    )}

                    {odataDebugResult.matching_actions?.length > 0 && (
                      <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200">
                        <p className="text-sm font-medium text-emerald-800 mb-2">
                          Actions correspondantes trouvées :
                        </p>
                        {odataDebugResult.matching_actions.map((action, i) => (
                          <div key={i} className="mb-2">
                            <p className="text-sm font-mono text-emerald-700">{action}</p>
                            {odataDebugResult.parameters?.[action] && (
                              <div className="mt-1 ml-4">
                                <p className="text-xs text-emerald-600 font-medium">Paramètres attendus :</p>
                                <ul className="list-disc list-inside text-xs text-emerald-700 font-mono">
                                  {odataDebugResult.parameters[action].map((p, j) => (
                                    <li key={j}>{p.name} ({p.type})</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {odataDebugResult.actions_found?.length > 0 && (
                      <details>
                        <summary className="text-xs font-medium text-slate-500 cursor-pointer hover:text-slate-700">
                          Toutes les actions trouvées ({odataDebugResult.actions_found.length})
                        </summary>
                        <div className="mt-2 bg-slate-100 rounded p-3 max-h-60 overflow-y-auto">
                          <ul className="text-xs font-mono space-y-1">
                            {odataDebugResult.actions_found.map((action, i) => (
                              <li key={i} className="text-slate-700">{action}</li>
                            ))}
                          </ul>
                        </div>
                      </details>
                    )}

                    {odataDebugResult.errors?.length > 0 && (
                      <div className="p-3 rounded-md bg-rose-50 border border-rose-200">
                        <p className="text-xs font-medium text-rose-700 mb-1">Erreurs :</p>
                        {odataDebugResult.errors.map((err, i) => (
                          <p key={i} className="text-xs text-rose-600">{err}</p>
                        ))}
                      </div>
                    )}

                    {/* Raw JSON output */}
                    <details>
                      <summary className="text-xs font-medium text-slate-500 cursor-pointer hover:text-slate-700">
                        Résultat brut (JSON)
                      </summary>
                      <textarea
                        readOnly
                        className="mt-2 w-full h-60 text-xs font-mono bg-slate-900 text-green-400 p-3 rounded-lg"
                        value={JSON.stringify(odataDebugResult, null, 2)}
                      />
                    </details>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Sync Results Dialog */}
      <Dialog open={!!syncResult} onOpenChange={() => setSyncResult(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              {syncResult?.status === "success" ? (
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              ) : syncResult?.status === "partial" ? (
                <AlertCircle className="w-5 h-5 text-amber-500" />
              ) : (
                <XCircle className="w-5 h-5 text-red-500" />
              )}
              Résultat de la synchronisation
            </DialogTitle>
            <DialogDescription>
              {syncResult?.message}
            </DialogDescription>
          </DialogHeader>

          {/* Sync Mode Info */}
          {syncResult?.details && (
            <div className="bg-slate-50 rounded-lg p-3 mt-2 text-xs space-y-1">
              {syncResult.details.sync_mode === "create" ? (
                <>
                  <p className="font-medium text-slate-700">Mode : Création</p>
                  <p className="text-slate-500">
                    Pas de clés de correspondance configurées. Tous les enregistrements source sont créés sur la destination.
                  </p>
                </>
              ) : (
                <>
                  <p className="font-medium text-slate-700">Mode : Correspondance par nom</p>
                  <p>
                    <span className="text-[#FF7A59]">HubSpot:</span> <code className="bg-white px-1 rounded">{syncResult.details.matching_key_hs}</code>
                    {" — "}
                    <span className="font-medium">{syncResult.details.hs_with_key ?? "?"}/{syncResult.details.hubspot_records}</span> records avec valeur
                  </p>
                  <p>
                    <span className="text-[#00B7C3]">BC:</span> <code className="bg-white px-1 rounded">{syncResult.details.matching_key_bc}</code>
                    {" — "}
                    <span className="font-medium">{syncResult.details.bc_with_key ?? "?"}/{syncResult.details.bc_records}</span> records avec valeur
                  </p>
                  {((syncResult.details.hs_with_key ?? 0) < (syncResult.details.hubspot_records ?? 0) / 2) && (
                    <p className="text-amber-600 font-medium mt-1">
                      La clé HubSpot est vide pour la plupart des records. Vérifiez que le bon champ est sélectionné dans Field Mapping.
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {/* Summary Stats */}
          {syncResult?.details && (
            <div className="grid grid-cols-5 gap-2 mt-2">
              <div className="bg-slate-50 p-3 rounded-lg text-center">
                <p className="text-lg font-bold text-slate-900">{syncResult.details.hubspot_records ?? 0}</p>
                <p className="text-xs text-slate-500">Records HS</p>
              </div>
              <div className="bg-slate-50 p-3 rounded-lg text-center">
                <p className="text-lg font-bold text-slate-900">{syncResult.details.bc_records ?? 0}</p>
                <p className="text-xs text-slate-500">Records BC</p>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg text-center">
                <p className="text-lg font-bold text-blue-700">{syncResult.details.records_updated ?? 0}</p>
                <p className="text-xs text-blue-600">Mis à jour</p>
              </div>
              <div className="bg-emerald-50 p-3 rounded-lg text-center">
                <p className="text-lg font-bold text-emerald-700">{syncResult.details.records_created ?? 0}</p>
                <p className="text-xs text-emerald-600">Créés</p>
              </div>
              {((syncResult.details.records_failed ?? 0) > 0) && (
                <div className="bg-red-50 p-3 rounded-lg text-center">
                  <p className="text-lg font-bold text-red-700">{syncResult.details.records_failed}</p>
                  <p className="text-xs text-red-600">Erreurs</p>
                </div>
              )}
            </div>
          )}

          {/* First errors summary */}
          {syncResult?.details?.first_errors?.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-2 space-y-1">
              <p className="text-xs font-medium text-red-700">Erreurs rencontrées :</p>
              {syncResult.details.first_errors.map((err, i) => (
                <p key={i} className="text-xs text-red-600 font-mono break-all">{err}</p>
              ))}
            </div>
          )}

          {/* Field-level results */}
          <div className="space-y-3 mt-2">
            <h4 className="text-sm font-medium text-slate-700">Détail par champ</h4>
            {syncResult?.details?.field_results?.map((field, i) => (
              <div key={i} className="p-3 rounded-lg border border-slate-200 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm font-medium truncate">{field.hubspot_field_label}</span>
                    <ArrowLeftRight className="w-3 h-3 text-slate-400 shrink-0" />
                    <span className="text-sm font-medium truncate">{field.bc_field_label}</span>
                  </div>
                  <Badge variant={field.status === "ok" ? "default" : "secondary"} className="shrink-0 ml-2">
                    {field.status === "ok" ? "Synchronisé" : "Pas de données"}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <p className="text-slate-500 mb-1">
                      {field.written_to_bc > 0 ? (
                        <span className="text-emerald-600">{field.written_to_bc} valeurs écrites vers BC</span>
                      ) : (
                        <span>Aucune écriture vers BC</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-500 mb-1">
                      {field.written_to_hs > 0 ? (
                        <span className="text-emerald-600">{field.written_to_hs} valeurs écrites vers HubSpot</span>
                      ) : (
                        <span>Aucune écriture vers HubSpot</span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Record-level details */}
          {syncResult?.details?.record_details?.length > 0 && (
            <div className="space-y-2 mt-4">
              <h4 className="text-sm font-medium text-slate-700">Détail par enregistrement</h4>
              <div className="max-h-60 overflow-y-auto space-y-1">
                {syncResult.details.record_details.map((rd, i) => (
                  <div key={i} className={`text-xs p-2 rounded flex items-center gap-2 ${
                    rd.status === "updated" ? "bg-blue-50" :
                    rd.status === "created" ? "bg-emerald-50" :
                    rd.status === "skipped" ? "bg-slate-50" : "bg-red-50"
                  }`}>
                    <span className="font-mono truncate flex-1">{rd.matching_key}</span>
                    {rd.direction && (
                      <span className="text-slate-400 shrink-0">{rd.direction}</span>
                    )}
                    <Badge variant={
                      rd.status === "updated" ? "default" :
                      rd.status === "created" ? "default" :
                      rd.status === "skipped" ? "secondary" : "destructive"
                    } className={`shrink-0 text-[10px] ${rd.status === "created" ? "bg-emerald-100 text-emerald-700" : ""}`}>
                      {rd.status === "updated" ? "Mis à jour" :
                       rd.status === "created" ? "Créé" :
                       rd.status === "skipped" ? "Ignoré" : "Erreur"}
                    </Badge>
                    {rd.reason && <span className="text-slate-500 truncate">{rd.reason}</span>}
                    {rd.errors && rd.errors.map((e, j) => <span key={j} className="text-red-500 truncate max-w-xs">{e}</span>)}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Debug Log */}
          {syncResult?.details?.debug_log?.length > 0 && (
            <details className="mt-4">
              <summary className="text-xs font-medium text-slate-500 cursor-pointer hover:text-slate-700">
                Debug log ({syncResult.details.debug_log.length} entrées)
              </summary>
              <div className="mt-2 bg-slate-900 text-green-400 rounded-lg p-3 max-h-80 overflow-y-auto font-mono text-[11px] leading-relaxed">
                {syncResult.details.debug_log.map((line, i) => (
                  <div key={i} className={
                    line.includes("[ERROR]") || line.includes("[TRACEBACK]") ? "text-red-400" :
                    line.includes("[SWITCH]") || line.includes("[COVERAGE]") ? "text-yellow-300" :
                    line.includes("[RESULT]") ? "text-cyan-300" :
                    line.includes("[SAMPLE]") ? "text-purple-300" :
                    "text-green-400"
                  }>{line}</div>
                ))}
              </div>
            </details>
          )}
        </DialogContent>
      </Dialog>

      {/* Preview Sync Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Eye className="w-5 h-5 text-indigo-600" />
              Aperçu de la synchronisation
            </DialogTitle>
            <DialogDescription>
              Analyse des enregistrements avant synchronisation. Les doublons potentiels sont détectés par correspondance de nom.
            </DialogDescription>
          </DialogHeader>

          {previewResult && (
            <div className="space-y-4 mt-2">
              {/* Summary */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500 mb-1">Total enregistrements</p>
                  <div className="flex items-center gap-3 text-sm">
                    <span className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-[#FF7A59]" />
                      <strong>{previewResult.summary?.hs_total || 0}</strong> HubSpot
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-[#00B7C3]" />
                      <strong>{previewResult.summary?.bc_total || 0}</strong> BC
                    </span>
                  </div>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500 mb-1">Mode de synchronisation</p>
                  <p className="text-sm font-medium">
                    {previewResult.summary?.sync_mode === "update_only" ? "Mise à jour uniquement" : "Création + mise à jour"}
                  </p>
                </div>
              </div>

              {/* HubSpot → BC */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-[#FF7A59]/10 px-4 py-2 border-b flex items-center justify-between">
                  <span className="font-medium text-sm text-slate-700">HubSpot → Business Central</span>
                  <div className="flex gap-2 text-xs">
                    <Badge variant="outline" className="bg-white">
                      {previewResult.summary?.hs_to_bc?.updates || 0} mises à jour
                    </Badge>
                    <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                      {previewResult.summary?.hs_to_bc?.creates || 0} créations
                    </Badge>
                    {(previewResult.summary?.hs_to_bc?.potential_duplicates || 0) > 0 && (
                      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                        {previewResult.summary?.hs_to_bc?.potential_duplicates} doublons potentiels
                      </Badge>
                    )}
                    {(previewResult.summary?.hs_to_bc?.skips || 0) > 0 && (
                      <Badge variant="secondary">
                        {previewResult.summary?.hs_to_bc?.skips} ignorés
                      </Badge>
                    )}
                  </div>
                </div>
                {/* Potential duplicates warning */}
                {previewResult.hs_to_bc?.potential_duplicates?.length > 0 && (
                  <div className="bg-amber-50 border-b border-amber-200 p-3">
                    <p className="text-xs font-medium text-amber-800 mb-2 flex items-center gap-1.5">
                      <AlertCircle className="w-3.5 h-3.5" />
                      Doublons potentiels détectés ({previewResult.hs_to_bc.potential_duplicates.length})
                    </p>
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {previewResult.hs_to_bc.potential_duplicates.slice(0, 10).map((rec, i) => (
                        <div key={i} className="text-xs flex items-center gap-2 text-amber-700">
                          <span className="font-mono bg-amber-100 px-1 rounded">{rec.name || "?"}</span>
                          <span className="text-amber-500">→</span>
                          <span className="text-slate-600">{rec.duplicate_candidate?.reason}</span>
                        </div>
                      ))}
                      {previewResult.hs_to_bc.potential_duplicates.length > 10 && (
                        <p className="text-xs text-amber-600">... et {previewResult.hs_to_bc.potential_duplicates.length - 10} autres</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* BC → HubSpot */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-[#00B7C3]/10 px-4 py-2 border-b flex items-center justify-between">
                  <span className="font-medium text-sm text-slate-700">Business Central → HubSpot</span>
                  <div className="flex gap-2 text-xs">
                    <Badge variant="outline" className="bg-white">
                      {previewResult.summary?.bc_to_hs?.updates || 0} mises à jour
                    </Badge>
                    <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                      {previewResult.summary?.bc_to_hs?.creates || 0} créations
                    </Badge>
                    {(previewResult.summary?.bc_to_hs?.potential_duplicates || 0) > 0 && (
                      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                        {previewResult.summary?.bc_to_hs?.potential_duplicates} doublons potentiels
                      </Badge>
                    )}
                    {(previewResult.summary?.bc_to_hs?.skips || 0) > 0 && (
                      <Badge variant="secondary">
                        {previewResult.summary?.bc_to_hs?.skips} ignorés
                      </Badge>
                    )}
                  </div>
                </div>
                {/* Potential duplicates warning */}
                {previewResult.bc_to_hs?.potential_duplicates?.length > 0 && (
                  <div className="bg-amber-50 border-b border-amber-200 p-3">
                    <p className="text-xs font-medium text-amber-800 mb-2 flex items-center gap-1.5">
                      <AlertCircle className="w-3.5 h-3.5" />
                      Doublons potentiels détectés ({previewResult.bc_to_hs.potential_duplicates.length})
                    </p>
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {previewResult.bc_to_hs.potential_duplicates.slice(0, 10).map((rec, i) => (
                        <div key={i} className="text-xs flex items-center gap-2 text-amber-700">
                          <span className="font-mono bg-amber-100 px-1 rounded">{rec.name || "?"}</span>
                          <span className="text-amber-500">→</span>
                          <span className="text-slate-600">{rec.duplicate_candidate?.reason}</span>
                        </div>
                      ))}
                      {previewResult.bc_to_hs.potential_duplicates.length > 10 && (
                        <p className="text-xs text-amber-600">... et {previewResult.bc_to_hs.potential_duplicates.length - 10} autres</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Warning if creates > 0 and mode is not update_only */}
              {previewResult.summary?.sync_mode !== "update_only" &&
               ((previewResult.summary?.hs_to_bc?.creates || 0) + (previewResult.summary?.bc_to_hs?.creates || 0)) > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
                  <Info className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                  <div className="text-xs text-blue-800">
                    <p className="font-medium">Des enregistrements seront créés</p>
                    <p className="mt-0.5">
                      Pour éviter les créations et ne faire que des mises à jour, passez en mode "Mise à jour uniquement"
                      dans la page Mapping (section Clés de correspondance).
                    </p>
                  </div>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex items-center justify-end gap-3 pt-2 border-t">
                <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>
                  Annuler
                </Button>
                <Button
                  onClick={() => previewResult?.scheduleKey && handleManualSync(previewResult.scheduleKey)}
                  disabled={syncing}
                  className="gap-1.5"
                >
                  {syncing ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Play className="w-4 h-4" />
                  )}
                  Lancer la synchronisation
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
